import React from 'react';

import { currentUser } from '@admin/config/current_user';
import { client } from '@admin/libraries/apollo';
import {
  WarehouseTask__State,
  WarehouseTask__ViewMode,
  useWarehousesQuery,
  useWarehousesSelectorQuery,
} from '@admin/schema';

import { defaultWarehouseTaskViewMode, showAtMyWarehousesViewMode } from './view_modes';

interface FiltersState {
  warehouseID?: string;
  state?: WarehouseTask__State;
  search?: string;
  startDate?: string;
  endDate?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  viewMode?: WarehouseTask__ViewMode;
  accountID?: string;
  page?: number;
}

interface FiltersProps {
  filters: FiltersState;
  onChange: (filters: FiltersState) => void;
}

const STATES = [
  { value: WarehouseTask__State.Pending, label: 'Pending' },
  { value: WarehouseTask__State.InProgress, label: 'In Progress' },
  { value: WarehouseTask__State.Completed, label: 'Completed' },
  { value: WarehouseTask__State.Canceled, label: 'Canceled' },
];

const DEFAULT_FILTERS: FiltersState = { viewMode: defaultWarehouseTaskViewMode() };

export { DEFAULT_FILTERS };
export type { FiltersState };

export const Filters: React.FC<FiltersProps> = ({ filters, onChange }) => {
  const { data: warehousesData } = useWarehousesQuery({ client });
  const viewMode = filters.viewMode || defaultWarehouseTaskViewMode();
  const atMyWarehouses = viewMode === WarehouseTask__ViewMode.AtMyWarehouses;
  const atMyWarehousesVisible = showAtMyWarehousesViewMode();
  const regionIDs = currentUser.region_ids?.map(String);
  const { data: managedWarehousesData } = useWarehousesSelectorQuery({
    client,
    variables: { filters: regionIDs?.length ? { regionIDs } : undefined },
    skip: !atMyWarehouses || !regionIDs?.length,
  });
  const warehouseOptions = atMyWarehouses
    ? (managedWarehousesData?.warehouses ?? [])
    : (warehousesData?.warehouses ?? []);

  const setViewMode = (nextViewMode: WarehouseTask__ViewMode) => {
    onChange({
      ...filters,
      viewMode: nextViewMode,
      warehouseID: nextViewMode === WarehouseTask__ViewMode.AtMyWarehouses ? filters.warehouseID : undefined,
      page: 1,
    });
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="col-md-12">
          <div className="btn-group" role="group" aria-label="Task view mode" style={{ marginBottom: 8 }}>
            <button
              type="button"
              className={`btn btn-sm ${viewMode === WarehouseTask__ViewMode.CreatedByMe ? 'btn-primary' : 'btn-default'}`}
              onClick={() => setViewMode(WarehouseTask__ViewMode.CreatedByMe)}
            >
              Created by me
            </button>
            {atMyWarehousesVisible && (
              <button
                type="button"
                className={`btn btn-sm ${viewMode === WarehouseTask__ViewMode.AtMyWarehouses ? 'btn-primary' : 'btn-default'}`}
                onClick={() => setViewMode(WarehouseTask__ViewMode.AtMyWarehouses)}
              >
                At my warehouse(s)
              </button>
            )}
            <button
              type="button"
              className={`btn btn-sm ${viewMode === WarehouseTask__ViewMode.All ? 'btn-primary' : 'btn-default'}`}
              onClick={() => setViewMode(WarehouseTask__ViewMode.All)}
            >
              All
            </button>
          </div>
        </div>
      </div>
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search by task ID, account, or customer..."
            aria-label="Search by task ID, account, or customer"
            value={filters.search || ''}
            onChange={(e) => onChange({ ...filters, search: e.target.value || undefined, page: 1 })}
          />
        </div>
        {viewMode === WarehouseTask__ViewMode.AtMyWarehouses && (
          <div className="col-md-3">
            <select
              className="form-control"
              aria-label="Warehouse"
              value={filters.warehouseID || ''}
              onChange={(e) => onChange({ ...filters, warehouseID: e.target.value || undefined, page: 1 })}
            >
              <option value="">All warehouses in my regions</option>
              {warehouseOptions.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {viewMode !== WarehouseTask__ViewMode.AtMyWarehouses && (
          <div className="col-md-3">
            <select
              className="form-control"
              aria-label="Warehouse"
              value={filters.warehouseID || ''}
              onChange={(e) => onChange({ ...filters, warehouseID: e.target.value || undefined, page: 1 })}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="col-md-3">
          <select
            className="form-control"
            aria-label="State"
            value={filters.state || ''}
            onChange={(e) =>
              onChange({
                ...filters,
                state: (e.target.value as WarehouseTask__State) || undefined,
                page: 1,
              })
            }
          >
            <option value="">All States</option>
            {STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row" style={{ marginBottom: 8 }}>
        <div className="col-md-2">
          <label className="control-label" style={{ fontSize: 11, marginBottom: 2 }}>
            Created From
          </label>
          <input
            type="date"
            className="form-control"
            value={filters.startDate || ''}
            onChange={(e) => onChange({ ...filters, startDate: e.target.value || undefined, page: 1 })}
          />
        </div>
        <div className="col-md-2">
          <label className="control-label" style={{ fontSize: 11, marginBottom: 2 }}>
            Created To
          </label>
          <input
            type="date"
            className="form-control"
            value={filters.endDate || ''}
            onChange={(e) => onChange({ ...filters, endDate: e.target.value || undefined, page: 1 })}
          />
        </div>
        <div className="col-md-2">
          <label className="control-label" style={{ fontSize: 11, marginBottom: 2 }}>
            Due From
          </label>
          <input
            type="date"
            className="form-control"
            value={filters.dueDateFrom || ''}
            onChange={(e) => onChange({ ...filters, dueDateFrom: e.target.value || undefined, page: 1 })}
          />
        </div>
        <div className="col-md-2">
          <label className="control-label" style={{ fontSize: 11, marginBottom: 2 }}>
            Due To
          </label>
          <input
            type="date"
            className="form-control"
            value={filters.dueDateTo || ''}
            onChange={(e) => onChange({ ...filters, dueDateTo: e.target.value || undefined, page: 1 })}
          />
        </div>
        <div className="col-md-2">
          <label className="control-label" style={{ fontSize: 11, marginBottom: 2 }}>
            Account ID
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Account ID"
            value={filters.accountID || ''}
            onChange={(e) => onChange({ ...filters, accountID: e.target.value || undefined, page: 1 })}
          />
        </div>
        <div className="col-md-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button className="btn btn-default btn-block" onClick={() => onChange(DEFAULT_FILTERS)}>
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};
